import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Upload, FileSpreadsheet, Search, Trash2, AlertTriangle } from "lucide-react";
import { formatMoney, RECONCILIATION_MODE_LABELS } from "@finora/config";
import { ReconciliationMode } from "@finora/types";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { ObradaIzvodaClientTab } from "./ObradaIzvodaClientTab";

export function BankStatementsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [payerSearchInput, setPayerSearchInput] = useState("");
  const [payerSearch, setPayerSearch] = useState("");
  const [mainTab, setMainTab] = useState<"izvodi" | "obrada">("izvodi");

  useEffect(() => {
    const timeout = setTimeout(() => setPayerSearch(payerSearchInput), 300);
    return () => clearTimeout(timeout);
  }, [payerSearchInput]);

  const { data: payerResults, isFetching: isSearchingPayer } = useQuery({
    queryKey: ["bank-statement-lines-search", payerSearch],
    queryFn: () => api.searchBankStatementLines(payerSearch),
    enabled: payerSearch.trim().length >= 2,
  });

  const {
    data: statements,
    isLoading,
    isError: isStatementsError,
    refetch: refetchStatements,
  } = useQuery({
    queryKey: ["bank-statements"],
    queryFn: () => api.listBankStatements(),
  });

  const { data: modeData } = useQuery({
    queryKey: ["reconciliation-mode"],
    queryFn: () => api.getReconciliationMode(),
  });

  const setMode = useMutation({
    mutationFn: (mode: ReconciliationMode) => api.setReconciliationMode(mode),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["reconciliation-mode"] }),
  });

  const upload = useMutation({
    mutationFn: (file: File) => api.importBankStatement(file),
    onSuccess: (statement) => {
      queryClient.invalidateQueries({ queryKey: ["bank-statements"] });
      setUploadError(null);
      navigate(`/izvodi/${statement.id}`);
    },
    onError: () => setUploadError("Uvoz nije uspeo. Proverite da li je fajl ispravan XML izvod."),
  });

  const deleteStatement = useMutation({
    mutationFn: (id: string) => api.deleteBankStatement(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["bank-statements"] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Izvodi</h1>
          <p className="text-sm text-slate-500">
            Uvoz bankovnih izvoda i automatsko/ručno uparivanje uplata sa fakturama
          </p>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xml"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate(file);
              e.target.value = "";
            }}
          />
          <Button onClick={() => fileInputRef.current?.click()} disabled={upload.isPending}>
            <Upload className="h-4 w-4" aria-hidden />
            {upload.isPending ? "Uvozim..." : "Uvezi izvod (XML)"}
          </Button>
        </div>
      </div>

      {uploadError && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{uploadError}</p>
      )}

      <div className="flex gap-1 border-b border-border">
        <button
          onClick={() => setMainTab("izvodi")}
          className={`border-b-2 px-4 py-2 text-sm font-medium ${
            mainTab === "izvodi" ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Izvodi
        </button>
        <button
          onClick={() => setMainTab("obrada")}
          className={`border-b-2 px-4 py-2 text-sm font-medium ${
            mainTab === "obrada" ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Obrada izvoda
        </button>
      </div>

      {mainTab === "obrada" ? (
        <ObradaIzvodaClientTab />
      ) : (
        <>

      <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <p className="mb-2 text-sm font-medium text-slate-700">Režim usklađivanja uplata</p>
        <div className="flex flex-wrap gap-2">
          {Object.values(ReconciliationMode).map((mode) => (
            <button
              key={mode}
              onClick={() => setMode.mutate(mode)}
              className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                modeData?.reconciliationMode === mode
                  ? "bg-brand text-white"
                  : "border border-border text-slate-600 hover:bg-slate-50"
              }`}
            >
              {RECONCILIATION_MODE_LABELS[mode]}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-400">
          {modeData?.reconciliationMode === ReconciliationMode.AUTO &&
            "Uplate koje se 100% poklapaju (referenca + iznos) knjiže se automatski pri uvozu."}
          {modeData?.reconciliationMode === ReconciliationMode.CONFIRM &&
            "Sistem predlaže fakturu za svaku uplatu, ali uvek čeka vašu potvrdu pre knjiženja."}
          {modeData?.reconciliationMode === ReconciliationMode.MANUAL &&
            // Bug fix (28.09.2026, "bez automatskih predloga" je bilo
            // netačno): sistem I DALJE obeležava najverovatniju fakturu
            // (isti matcher kao kod ostalih režima - vidi
            // BankStatementsService.import/getCandidates, "Predloženo"
            // oznaka u modalu Proknjiži), samo je NIKAD ne knjiži sam -
            // razlika prema "Predlog + potvrda" režimu je samo kozmetička
            // (oba režima se ponašaju identično u pozadini), pa tekst ovde
            // ne sme da tvrdi da predloga uopšte nema.
            "Sami birate i potvrđujete fakturu za svaku uplatu - sistem može da obeleži najverovatniju, ali nikad ne knjiži bez vašeg izbora."}
        </p>
      </div>

      <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <p className="mb-2 text-sm font-medium text-slate-700">
          Pretraga po uplatiocu (kupcu) - u kojim izvodima se pojavljuje
        </p>
        <label className="relative block max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
          <input
            value={payerSearchInput}
            onChange={(e) => setPayerSearchInput(e.target.value)}
            placeholder="Ukucaj ime kupca/uplatioca..."
            className="h-10 w-full rounded-lg border border-border pl-9 pr-3 text-sm focus:border-brand focus:outline-none"
          />
        </label>
        {payerSearch.trim().length >= 2 && (
          <div className="mt-3">
            {isSearchingPayer ? (
              <p className="text-sm text-slate-400">Pretraga...</p>
            ) : !payerResults || payerResults.length === 0 ? (
              <p className="text-sm text-slate-400">Nema pronađenih uplata za "{payerSearch}".</p>
            ) : (
              <ul className="divide-y divide-border">
                {payerResults.map((line) => (
                  <li
                    key={line.id}
                    onClick={() => navigate(`/izvodi/${line.statementId}`)}
                    className="flex cursor-pointer items-center justify-between py-2 text-sm hover:bg-slate-50"
                  >
                    <div>
                      <span className="font-medium text-slate-800">{line.payerName ?? "—"}</span>
                      <span className="ml-2 text-slate-400">
                        Izvod {line.statementNumber ?? "?"} ·{" "}
                        {new Date(line.statementDate).toLocaleDateString("sr-RS")}
                      </span>
                    </div>
                    <span className={line.direction === "CREDIT" ? "text-emerald-700" : "text-slate-500"}>
                      {line.direction === "CREDIT" ? "+" : "-"}
                      {formatMoney(line.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : isStatementsError ? (
          // 30.09.2026 (FINORA-IMP-015 - "ko mi jede izvode?"): dosad se
          // SVAKA greška učitavanja liste (mrežni prekid, restart servera,
          // modul postao neaktivan) tiho prikazivala kao "Još nema uvezenih
          // izvoda" - identično kao da su izvodi stvarno nestali, iako su i
          // dalje u bazi. Greška se sada eksplicitno razlikuje od "prazno".
          <div className="p-5">
            <div className="flex items-start gap-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
              <div>
                <p className="font-medium">Nije moguće učitati listu izvoda.</p>
                <p className="mt-0.5 text-red-600">
                  Ovo NE znači da su izvodi obrisani - najverovatnije je privremen problem sa
                  serverom ili mrežom.{" "}
                  <button onClick={() => refetchStatements()} className="font-medium underline">
                    Pokušaj ponovo
                  </button>
                </p>
              </div>
            </div>
          </div>
        ) : !statements || statements.length === 0 ? (
          <div className="p-5">
            <EmptyState
              icon={FileSpreadsheet}
              title="Još nema uvezenih izvoda"
              description="Uvezite XML izvod dugmetom iznad da počnete usklađivanje uplata."
            />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Broj izvoda</th>
                <th>Datum</th>
                <th>Prethodno stanje</th>
                <th>Novo stanje</th>
                <th>Stavki</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {statements.map((s) => (
                <tr
                  key={s.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => navigate(`/izvodi/${s.id}`)}
                >
                  <td className="font-medium text-slate-800">{s.statementNumber ?? "—"}</td>
                  <td className="text-slate-500">
                    {new Date(s.statementDate).toLocaleDateString("sr-RS")}
                  </td>
                  <td>{formatMoney(s.previousBalance)}</td>
                  <td>{formatMoney(s.newBalance)}</td>
                  <td className="text-slate-500">{s.lines.length}</td>
                  <td>
                    {s.reviewStatus === "PENDING_REVIEW" ? (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                        Na čekanju (pregled)
                      </span>
                    ) : (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                        Odobreno
                      </span>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => {
                        if (window.confirm("Obrisati ovaj izvod i sve njegove stavke?")) {
                          deleteStatement.mutate(s.id);
                        }
                      }}
                      className="text-slate-400 hover:text-red-600"
                      title="Obriši izvod"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
        </>
      )}
    </div>
  );
}
